// FR-2 / FR-3 scoring formulas, implemented exactly as specified.

import type { AssessmentFrame, AssessmentResult } from '../types';

// ---- FR-2 pitch (continuous tone) ----
// 100 * exp(-0.015 * RMSE_cents); 0 when RMSE > 50 cents.
export function pitchScoreFromRMSE(rmseCents: number): number {
  if (rmseCents > 50) return 0;
  return 100 * Math.exp(-0.015 * rmseCents);
}

// FR-2 duration: min(1, actual/target); 0 when actual < 0.5 * target.
export function timeScoreFromDuration(actual: number, target: number): number {
  if (target <= 0) return 0;
  if (actual < 0.5 * target) return 0;
  return Math.min(1, actual / target);
}

// FR-2 single-note click mode.
export function singleNoteScore(centsDev: number, timedOut = false): number {
  if (timedOut) return 0;
  const abs = Math.abs(centsDev);
  if (abs <= 30) return 100;
  if (abs <= 50) return 60;
  return 0;
}

// FR-2 combined practice score: 0.6 pitch + 0.3 time + 0.1 onset clarity.
export function combinedPracticeScore(pitch: number, time: number, clarity01: number): number {
  return 0.6 * pitch + 0.3 * time * 100 + 0.1 * clarity01 * 100;
}

// FR-2.1 bowing steadiness from pitch & volume jitter (variance), normalised 0..1.
export function bowingSteadiness(centsSeries: number[], rmsSeries: number[]): number {
  if (centsSeries.length < 2) return 0;
  const centsVar = variance(centsSeries);
  const rmsVar = variance(rmsSeries);
  const meanRms = mean(rmsSeries) || 1;
  // Lower variance -> steadier. Map cents variance: 0 -> 1, 400 -> 0.
  const pitchSteady = Math.max(0, 1 - centsVar / 400);
  const volSteady = Math.max(0, 1 - (rmsVar / (meanRms * meanRms)) / 2);
  return 0.5 * pitchSteady + 0.5 * volSteady;
}

// ---- FR-3 timbre ----
// Harmonic Energy Ratio score: 100*(1 - |HER - 0.72|/0.15); 0 outside [0.6,0.85]-ish range.
export function herScore(her: number): number {
  const score = 100 * (1 - Math.abs(her - 0.72) / 0.15);
  return Math.max(0, Math.min(100, score));
}

// Resonance score: 100 * exp(-(d/800)^2), d = Euclidean distance to template.
export function resonanceScore(formants: [number, number, number], template: [number, number, number]): number {
  const d = Math.sqrt(
    (formants[0] - template[0]) ** 2 + (formants[1] - template[1]) ** 2 + (formants[2] - template[2]) ** 2,
  );
  return 100 * Math.exp(-((d / 800) ** 2));
}

// FR-3 timbreScore = 0.3 * herScore + 0.7 * resonanceScore.
export function timbreScore(her: number, formants: [number, number, number], template: [number, number, number]): number {
  return 0.3 * herScore(her) + 0.7 * resonanceScore(formants, template);
}

export interface SnrPolicy {
  weight: number; // 1, 0.5, or 0
  disabled: boolean;
  warn: boolean;
}

// FR-3.1 SNR three-tier circuit breaker.
export function snrPolicy(snr: number): SnrPolicy {
  if (snr <= 10) return { weight: 0, disabled: true, warn: false };
  if (snr <= 20) return { weight: 0.5, disabled: false, warn: true };
  return { weight: 1, disabled: false, warn: false };
}

// Aggregate a sequence of assessment frames into a result.
export function aggregateAssessment(frames: AssessmentFrame[]): AssessmentResult {
  const valid = frames.filter((f) => f.confidence > 0.3);
  if (valid.length === 0) {
    return { overallScore: 0, pitchScore: 0, details: frames };
  }
  const rmse = Math.sqrt(mean(valid.map((f) => f.centsDeviation ** 2)));
  const pitch = pitchScoreFromRMSE(rmse);
  const her = valid.filter((f) => f.harmonicEnergyRatio != null).map((f) => f.harmonicEnergyRatio as number);
  const timbre = her.length ? mean(her.map((h) => herScore(h))) : undefined;
  return {
    overallScore: pitch,
    pitchScore: pitch,
    timeScore: undefined,
    timbreScore: timbre,
    details: frames,
  };
}

function mean(xs: number[]): number {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
}
function variance(xs: number[]): number {
  if (xs.length < 2) return 0;
  const m = mean(xs);
  return mean(xs.map((x) => (x - m) ** 2));
}
